/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {Box, Divider, Flex, IconWithText, showMessage, Theme as ThemeWithoutType} from 'exsportia-components'
import {useFormikContext} from 'formik'
import {FC, memo, useCallback, useEffect, useMemo, useState} from 'react'
import {useQuery} from 'react-query'
import {useNavigate} from 'react-router-dom'

import {BookingQueries} from '../../api/booking'
import {useSubscription} from '../../api/subscriptions/hooks'
import {useVenue} from '../../api/venue/hooks'
import {CheckoutGateNotice} from '../../components/checkout-gate-notice/checkout-gate-notice'
import {EmptyList} from '../../components/empty-list/empty-list'
import {ExsportiaPayIcon, PaymentMethodsGroup} from '../../components/payment-icons'
import {PriceRow} from '../../components/price-row/price-row'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {SimpleGridLayoutHeader} from '../../layout/grid-layout/headers/simple-grid-layout-header'
import {useAuthState, useHandleLoginState} from '../../services/auth/hooks'
import {useBookingLoader} from '../../services/booking/hooks'
import {PaymentSubmit, PaymentTypes, usePaymentOptions} from '../../services/payments/hooks/use-payment-options'
import {ThemeType} from '../../theme/types'
import {Button} from '../../ui/components/button/button'
import {InfoMessage} from '../../ui/components/info-message/info-message'
import {RadioButton} from '../../ui/components/radio-button'
import {Toggle} from '../../ui/components/toggle'
import {useLocale} from '../../utils/i18n'
import {ClassSummarySection} from '../booking-summary/class-summary-section'
import {ServicePricingAndPaymentsSection, ServiceSummarySection} from '../booking-summary/service-summary-section'
import {BookingCommonType} from '../booking-summary/user-info-section'
import {CycleOption} from '../subscription-profile/helpers'
import {SubscriptionSummarySection} from '../subscription-profile/payment/subscription-summary-section'
import {UserPaymentsMethods} from './components/user-payments-type-modal'

const Theme = ThemeWithoutType as ThemeType

// TODO: move to separated component file
type Props = {
  paymentType: PaymentTypes
  isLoading: boolean
  onClick: () => void
  totalPrice: number
  disabled?: boolean
}

export const SubmitButton: FC<Props> = memo(({totalPrice, paymentType, isLoading, onClick, disabled = false}) => {
  const {l} = useLocale()

  const text =
    paymentType === PaymentTypes.PAY_ON_LOCATION || totalPrice === 0
      ? l('book')
      : paymentType === PaymentTypes.ONE_TIME
        ? l('continue')
        : l('pay')

  return (
    <Button
      disabled={isLoading || disabled}
      onClick={onClick}
      isLoading={isLoading}
    >
      {text}
    </Button>
  )
})

const Header: FC = memo(() => {
  const {l} = useLocale()
  return <SimpleGridLayoutHeader title={l('paymentMethod')} />
})

type PaymentEntityType = 'class' | 'service' | 'subscription'

type UserPaymentTypeModal = {
  type: PaymentEntityType
}

const typeToSummaryComponentMap: {
  [key in PaymentEntityType]: FC<{
    setIsLoading: (arg: boolean) => void
    setWholePrice?: (arg?: number) => void
    cyclesConfig?: CycleOption | null
  }>
} = {
  class: ClassSummarySection,
  service: ServiceSummarySection,
  subscription: SubscriptionSummarySection,
}

export const UserPaymentTypeModal: FC<UserPaymentTypeModal> = ({type}) => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const {data: venue} = useVenue()
  const {data: subscription} = useSubscription()
  const {submitForm, setValues, values: bookingState} = useFormikContext<BookingCommonType>()
  const entity = useMemo(() => {
    if (type !== 'subscription') {
      return bookingState[type]
    }
    return subscription
  }, [type])
  const handleSubmitWithPayment = useCallback(
    ({paymentMethodId}: { paymentMethodId: string | null }) => {
      setValues({...bookingState, paymentMethodId, paymentType: 'payNow'})
      submitForm().catch((e) => console.log(String(e)))
    },
    [bookingState, setValues, submitForm],
  )
  const {data: bookingPriceData} = useQuery(BookingQueries.calculateBookingPriceQuery({enabled: false, refetchOnWindowFocus: false}))
  const total = useMemo(() => bookingPriceData
  // `totalPrice` / `toPay` are after the promo code; `price` stays at list (D364 contract §3.2).
  ? bookingPriceData.reduce((acc, {toPay, totalPrice}) => Number(bookingState.splitPayment ? toPay : totalPrice) + acc, 0)
  : 0, [bookingPriceData, bookingState])
  const handleClickButton: PaymentSubmit = useCallback(
    ({paymentType, paymentMethodToken}) => {
      if (paymentType === PaymentTypes.PAY_NOW && total > 0) {
        return handleSubmitWithPayment({paymentMethodId: paymentMethodToken})
      }
      if (paymentType === PaymentTypes.ONE_TIME && total > 0) {
        setValues({...bookingState, paymentType: 'oneTimePayment'})
        submitForm().catch((e) => console.log(String(e)))
        return
      }
      setValues({...bookingState, paymentType: 'payOnLocation'})
      submitForm().catch((e) => console.log(String(e)))
    },
    [total, submitForm, bookingState, handleSubmitWithPayment],
  )
  const {
    paymentType,
    allowedOptions,
    setPaymentType,
    submitWithPayment,
    paymentMethodToken,
    setUserPaymentToken,
    isPayOnExsportiaAllowed,
    checkoutGate,
    isCheckingCharge,
  } =
    usePaymentOptions({
      submit: handleClickButton,
      // @ts-ignore
      entity, // TODO: fix type
      entityType: type,
      totalPrice: bookingPriceData ? total : undefined,
    })
  useEffect(() => {
    if (bookingState?.splitPayment === true && paymentType === PaymentTypes.PAY_ON_LOCATION) {
      if (isPayOnExsportiaAllowed) {
        setPaymentType(PaymentTypes.ONE_TIME)
      } else {
        showMessage(`${l('split_payment_among_participants')} not allowed for pay on location`, 'warn')
        setValues({
          ...bookingState,
          splitPayment: !bookingState?.splitPayment,
        })
      }
    }
  }, [bookingState?.splitPayment])
  const [isLoading, setIsLoading] = useState(true)
  const {isLoggedIn} = useAuthState()
  const [loggedInState, setLoggedInState] = useState(isLoggedIn)
  const {isLoading: isBookingLoading} = useBookingLoader()

  const handleLogOut = useCallback(() => {
    if (loggedInState) {
      navigate(-2)
      setValues({...bookingState, participants: []})
    }
    setLoggedInState(isLoggedIn)
  }, [bookingState])
  useHandleLoginState({
    handleLogOut,
  })
  const SummaryComponent = useMemo(() => typeToSummaryComponentMap[type], [type])
  const navigateBackToProfile = useCallback(() => {
    navigate('../')
  }, [])
  const setOneTimePayment = useCallback(() => setPaymentType(PaymentTypes.ONE_TIME), [])
  const setPayOnLocation = useCallback(() => setPaymentType(PaymentTypes.PAY_ON_LOCATION), [])

  return (
    <GridLayout header={<Header />}>
      {allowedOptions.length === 0 && !isLoading ? (
        <EmptyList
          onClick={navigateBackToProfile}
          text={l('byRequestOnly')}
          buttonText={l('back')}
        />
      ) : (
        <Box>
          {
            type === 'class'
            ? null
            : <ServicePricingAndPaymentsSection setIsLoading={setIsLoading} />
          }
          {bookingState.participants.length > 1 && bookingState?.service?.splitPayment === true && (
            <>
              <Flex
                justifyContent='space-between'
                py='12px'
              >
                <IconWithText
                  text={l('split_payment_among_participants', {caseAction: 'capitalize'})}
                  icon='paymentSplit'
                />
                <Toggle
                  styleType='small'
                  checked={bookingState?.splitPayment === true}
                  onChange={() => {
                    if (!isPayOnExsportiaAllowed) {
                      return null
                    }
                    setValues({
                      ...bookingState,
                      splitPayment: !bookingState?.splitPayment,
                      participants: bookingState?.participants?.map((participant, index) => {
                        if (index === 0) {
                          return {
                            ...participant,
                            toPay: true,
                          }
                        }
                        return {
                          ...participant,
                          toPay: false,
                        }
                      }),
                    })
                  }}
                />
              </Flex>
              <Box mb='12px'>
                <InfoMessage
                  text={l('split_payment_toggle_info')}
                  preset='success'
                />
              </Box>
            </>
          )}
          {/* With split payment on, the pay-on-location radio is disabled: no "another method" line. */}
          <CheckoutGateNotice
            gate={bookingState.splitPayment === true ? {...checkoutGate, hasOtherMethod: false} : checkoutGate}
          />
          {
            total > 0 && (
              <Box mb='16px'>
                {
                  allowedOptions.includes(PaymentTypes.PAY_ON_LOCATION)
                  && (
                    <Flex
                      alignItems='center'
                      height={Theme?.spaces?.multiplier6x}
                    >
                      <RadioButton
                        text={l('pay_on_location', {caseAction: 'capitalize'})}
                        disabled={bookingState?.splitPayment === true || !allowedOptions.includes(PaymentTypes.PAY_ON_LOCATION) || isLoading || isBookingLoading}
                        checked={paymentType === PaymentTypes.PAY_ON_LOCATION}
                        onChange={setPayOnLocation}
                      />
                    </Flex>
                  )
                }
                <Divider />
                {allowedOptions.includes(PaymentTypes.ONE_TIME) && (
                  <Flex
                    alignItems='center'
                    height={Theme?.spaces?.multiplier6x}
                  >
                    <RadioButton
                      disabled={isLoading || isBookingLoading || checkoutGate.isGated}
                      checked={paymentType === PaymentTypes.ONE_TIME}
                      onChange={setOneTimePayment}
                      text={l('exsportiaPay', {caseAction: 'capitalize'})}
                      iconBefore={<ExsportiaPayIcon w={40} h={24} />}
                      iconAfter={<Box maxWidth='35%' ml='auto'><PaymentMethodsGroup w={180} h={32} /></Box>}
                    />
                  </Flex>
                )}
                <Divider />
                {allowedOptions.includes(PaymentTypes.PAY_NOW) && (
                  <>
                    <Box>
                      <UserPaymentsMethods
                        isLoading={isLoading || isBookingLoading || checkoutGate.isGated}
                        venueId={venue?.uid}
                        paymentMethod={paymentMethodToken}
                        setPaymentMethod={setUserPaymentToken}
                      />
                    </Box>
                  </>
                )}
              </Box>
            )
          }
          <Flex my='16px'>
            <PriceRow
              bold
              text={l('toPay')}
              currency={venue?.currency || ''}
              price={total}
            />
          </Flex>
            <SubmitButton
              totalPrice={total}
              paymentType={paymentType}
              onClick={() => submitWithPayment()}
              isLoading={isLoading || isBookingLoading || isCheckingCharge}
              disabled={checkoutGate.isSelectionBlocked}
            />
        </Box>
      )}
      <Box>
        <SummaryComponent setIsLoading={setIsLoading} />
      </Box>
    </GridLayout>
  )
}
